import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ResearchCreateRequest, ResearchMode } from "../types";
import { RowBox } from "../../../utils/styledBox";

interface CreateResearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<ResearchCreateRequest, "ai_mode">) => Promise<void>;
  creating: boolean;
}

const CreateResearchDialog: React.FC<CreateResearchDialogProps> = ({ open, onClose, onSubmit, creating }) => {
  const { t } = useTranslation();
  const [createForm, setCreateForm] = useState<Omit<ResearchCreateRequest, "ai_mode">>({
    query: "",
    max_time: 60,
    research_mode: "fast",
  });
  const [createError, setCreateError] = useState("");

  const handleSubmit = async () => {
    if (!createForm.query.trim()) {
      setCreateError(t("deepResearch.errors.queryRequired"));
      return;
    }

    if (createForm.query.trim().length < 10) {
      setCreateError(t("deepResearch.errors.queryTooShort"));
      return;
    }

    setCreateError("");
    await onSubmit(createForm);
  };

  const handleClose = () => {
    if (!creating) {
      setCreateForm({ query: "", max_time: 60, research_mode: "fast" });
      setCreateError("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("deepResearch.create.title")}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={4}
              label={t("deepResearch.create.queryLabel")}
              placeholder={t("deepResearch.create.queryPlaceholder")}
              value={createForm.query}
              onChange={(e) => setCreateForm({ ...createForm, query: e.target.value })}
              sx={{ mb: 1, mt: 2 }}
              error={!!createError}
              helperText={createError || t("deepResearch.create.queryHelperText")}
              disabled={creating}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth disabled={creating}>
              <InputLabel>{t("deepResearch.create.researchModeLabel")}</InputLabel>
              <Select
                value={createForm.research_mode}
                label={t("deepResearch.create.researchModeLabel")}
                onChange={(e) => setCreateForm({ ...createForm, research_mode: e.target.value as ResearchMode })}
              >
                <MenuItem value="fast">{t("deepResearch.researchMode.fastDescription")}</MenuItem>
                <MenuItem value="detailed">{t("deepResearch.researchMode.detailedDescription")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={t("deepResearch.create.maxTimeLabel")}
              value={createForm.max_time}
              onChange={(e) => setCreateForm({ ...createForm, max_time: parseInt(e.target.value) || 60 })}
              inputProps={{ min: 15, max: 120 }}
              disabled={creating}
            />
          </Grid>
        </Grid>
        <RowBox sx={{ alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
          <Button disableElevation onClick={handleClose} disabled={creating} variant="outlined">
            {t("deepResearch.actions.cancel")}
          </Button>
          <Button
            disableElevation
            onClick={handleSubmit}
            variant="contained"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
          >
            {creating ? t("deepResearch.create.starting") : t("deepResearch.create.startResearch")}
          </Button>
        </RowBox>

        {creating && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t("deepResearch.create.startingMessage")}
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateResearchDialog;
